import React, { useMemo, useState } from 'react';
import { omit } from 'lodash';
import {
  BootstrapTable,
  Options,
  TableHeaderColumn,
} from 'react-bootstrap-table';
import { JsonLink } from './JsonLink';
import { CopyableCode } from './CopyableCode';
import {
  ApplicationOverviewDialog,
  ApplicationOverviewDialogData,
  FinanceApplicationOverviewData,
} from './ApplicationOverviewDialog';
import './CreditApplications.scss';
import './ApplicationOverview.scss';
import { GetLendingSupportDealFinanceOfferResponse } from '../../hooks/services';
import { sortFinanceResponses } from './sortFinanceResponses';

type TableRowData = {
  car: GetLendingSupportDealFinanceOfferResponse[number]['car'];
  carId: string;
  createdAt: Date;
  financeApplication: Omit<
    GetLendingSupportDealFinanceOfferResponse[number],
    'financeResponses' | 'upstartApplicationStatus'
  >;
  financeResponses: GetLendingSupportDealFinanceOfferResponse[number]['financeResponses'];
  hasSelectedResponse: boolean;
  provider: string;
  providerApplicationId: string;
  upstartApplicationId: string;
  upstartApplicationStatus: GetLendingSupportDealFinanceOfferResponse[number]['upstartApplicationStatus'];
};

interface CreditApplicationsProps {
  creditAppList: GetLendingSupportDealFinanceOfferResponse;
  onClick: (data?: object | null) => void;
}

/**
 * This component renders the credit application section of the lending support tool,
 * showing the list of finance applications, responses, and their statuses.
 */
export const CreditApplications: React.FC<CreditApplicationsProps> = ({
  creditAppList,
  onClick,
}) => {
  const [applicationOverviewData, setApplicationOverviewData] =
    useState<ApplicationOverviewDialogData | null>(null);

  const data = useMemo<TableRowData[]>(
    () =>
      creditAppList.map((financeApplication): TableRowData => {
        const { car } = financeApplication;
        const { id: provider, applicationId: providerApplicationId } =
          financeApplication.provider;
        return {
          car,
          carId: car.id,
          createdAt: new Date(financeApplication.createdAt ?? 0),
          financeApplication: omit(
            financeApplication,
            'financeResponses',
            'upstartApplicationStatus',
          ),
          financeResponses: sortFinanceResponses(
            financeApplication.financeResponses,
          ),
          hasSelectedResponse: financeApplication.financeResponses.some(
            (r) => r.isSelected,
          ),
          provider,
          providerApplicationId,
          upstartApplicationId:
            provider === 'upstart' ? providerApplicationId : '',
          upstartApplicationStatus: financeApplication.upstartApplicationStatus,
        };
      }),
    [creditAppList],
  );

  const formatCar = (_cell: string, row: TableRowData) => {
    const { car } = row;
    const carName =
      [car.year, car.make, car.model].filter(Boolean).join(' ') || car.id;
    return (
      <div>
        <JsonLink data={car} onClick={() => onClick(car)} text={carName} />
        <div>
          <CopyableCode value={car.id} />
        </div>
      </div>
    );
  };

  const formatApplicationOverview = (
    _financeApplication: TableRowData['financeApplication'],
    row: TableRowData,
  ) => {
    const { initialSubmissionData, car } = row.financeApplication;
    if (row.provider !== 'upstart' || !initialSubmissionData) return <span />;
    const requestedTerms: FinanceApplicationOverviewData = {
      ...initialSubmissionData,
      carPrice: car?.price,
    };
    return (
      <span
        className="lending-support-link"
        onClick={() =>
          setApplicationOverviewData({
            requestedTerms,
            responses: row.financeResponses ?? [],
          })
        }
      >
        Overview
      </span>
    );
  };

  const formatFinanceApplication = (
    providerApplicationId: string,
    row: TableRowData,
  ) => (
    <div>
      <JsonLink
        data={row.financeApplication}
        text="JSON"
        onClick={() => onClick(row.financeApplication)}
      />
      <div>{row.financeApplication.status}</div>
      <div>
        <CopyableCode value={providerApplicationId} />
      </div>
    </div>
  );

  const formatUpstartLinks = (upstartApplicationId: string) => {
    const adminBaseUrl = process.env.REACT_APP_UPSTART_ADMIN_URL;
    if (!adminBaseUrl || !upstartApplicationId) {
      return <span />;
    }
    return (
      <div>
        <a
          className="lending-support-link lending-support-link-inline"
          href={`${adminBaseUrl}/admin/auto_retail_admin/application_search/${upstartApplicationId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Auto Retail
        </a>
        {' | '}
        <a
          className="lending-support-link lending-support-link-inline"
          href={`${adminBaseUrl}/admin/applications/${upstartApplicationId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Upstart
        </a>
      </div>
    );
  };

  const formatFinanceResponses = (
    responses: TableRowData['financeResponses'],
  ) => (
    <div>
      {responses?.map((financeResponse) => (
        <div
          key={financeResponse.id}
          data-testid={`finance-response-${financeResponse.id}`}
        >
          <JsonLink
            data={financeResponse}
            text="JSON"
            isSelected={financeResponse.isSelected}
            onClick={() => onClick(financeResponse)}
          />
          <div>{financeResponse.status}</div>
          {financeResponse.initialSubmissionData?.primaryBadgeValue && (
            <div className="lending-support-code">
              {financeResponse.initialSubmissionData.primaryBadgeValue}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const formatApplicationStatus = (
    fas: TableRowData['upstartApplicationStatus'],
  ) => {
    if (!fas) {
      return <div>n/a</div>;
    }
    return (
      <div>
        <JsonLink data={fas} text="DAPI status" onClick={() => onClick(fas)} />
        <div>{fas.code}</div>
      </div>
    );
  };

  if (!creditAppList?.length) {
    return (
      <div className="lending-support-credit-apps-empty-container">n/a</div>
    );
  }
  const options: Options = {
    defaultSortName: 'createdAt',
    defaultSortOrder: 'desc',
  };

  return (
    <>
      <ApplicationOverviewDialog
        open={!!applicationOverviewData}
        data={applicationOverviewData}
        onClose={() => setApplicationOverviewData(null)}
      />
      <BootstrapTable
        data={data}
        options={options}
        striped
        trClassName={(row: TableRowData) =>
          row.hasSelectedResponse ? 'lending-support-row-selected' : ''
        }
      >
        <TableHeaderColumn
          dataField="createdAt"
          dataFormat={(d) => d.toLocaleString()}
          dataSort
        >
          Created
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="carId"
          dataFormat={formatCar}
          dataSort
          filter={{ type: 'TextFilter', placeholder: 'Enter VIN' }}
        >
          Vehicle
        </TableHeaderColumn>
        <TableHeaderColumn dataField="provider">Provider</TableHeaderColumn>
        <TableHeaderColumn
          dataField="financeApplication"
          dataFormat={formatApplicationOverview}
        >
          Application Overview
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="providerApplicationId"
          dataFormat={formatFinanceApplication}
          isKey
          width={'26em'}
        >
          Provider Application
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="financeResponses"
          dataFormat={formatFinanceResponses}
        >
          Provider Responses
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="upstartApplicationStatus"
          dataFormat={formatApplicationStatus}
        >
          Application Status
        </TableHeaderColumn>
        <TableHeaderColumn
          dataField="upstartApplicationId"
          dataFormat={formatUpstartLinks}
        >
          Upstart Admin Links
        </TableHeaderColumn>
      </BootstrapTable>
    </>
  );
};
